import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import { Link, useNavigate } from 'react-router-dom'
import { adminService, type EventSummary } from '../../services/adminService'
import { LoadingState, EmptyState } from '../../components/ui/States'
import { formatDateRange } from '../../utils/format'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import EventsCalendar from '../components/EventsCalendar'

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  published: { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  draft: { color: '#B5611A', bg: 'rgba(228,119,32,0.15)' },
  archived: { color: '#707070', bg: 'rgba(112,112,112,0.12)' },
}

export default function AdminEventsPage() {
  const [events, setEvents] = useState<EventSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'list' | 'calendar'>('list')
  const navigate = useNavigate()
  const { notify } = useToast()

  useEffect(() => {
    adminService
      .listEvents()
      .then(setEvents)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [notify])

  return (
    <Container maxWidth={view === 'calendar' ? 'lg' : 'md'} sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Events
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage your event experiences
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next) => {
              if (next) setView(next)
            }}
          >
            <ToggleButton value="list" aria-label="List view">
              <ViewListRoundedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="Calendar view">
              <CalendarMonthRoundedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<DashboardCustomizeRoundedIcon />}
            onClick={() => navigate('/admin/templates')}
          >
            New from template
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => navigate('/admin/events/new')}
          >
            New event
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <LoadingState />
      ) : events.length === 0 ? (
        <EmptyState
          icon={<EventRoundedIcon fontSize="large" />}
          title="No events yet"
          description="Create your first event to get started."
          action={
            <Button variant="contained" onClick={() => navigate('/admin/events/new')}>
              New event
            </Button>
          }
        />
      ) : view === 'calendar' ? (
        <EventsCalendar events={events} />
      ) : (
        <Stack spacing={1.5}>
          {events.map((ev) => {
            const sc = STATUS_COLORS[ev.status] ?? STATUS_COLORS.draft
            return (
              <Card
                key={ev.id}
                variant="outlined"
                component={Link}
                to={`/admin/events/${ev.id}`}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderRadius: 3,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {ev.name}
                    </Typography>
                    <Chip
                      label={ev.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        color: sc.color,
                        bgcolor: sc.bg,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {formatDateRange(ev.startsAt, ev.endsAt)}
                    {ev.location ? ` · ${ev.location}` : ''}
                  </Typography>
                </Box>
                <ChevronRightRoundedIcon color="action" />
              </Card>
            )
          })}
        </Stack>
      )}
    </Container>
  )
}
